import type { FilterComponentProps } from '@/app/common/filters/core/filterComponentType.ts';
import type { FilterSearchDynamicSelected } from '@/app/common/filters/types/searchDynamic/FilterSearchDynamicSelector.tsx';
import type {
  AutocompleteHotelAPIFullResponse,
  AutocompleteHotelPayload,
} from '@/app/components/search/components/Filters/verticalFilters/Fields/getProprtyNameField/PropertyNameSelector.tsx';
import type { AutocompleteHotel } from '@/app/components/search/utils/api/autocompleteHotelAPIResponseTypes.ts';
import type { FilterDataSearchDynamic } from '@/app/common/filters/core/filtersTypes.ts';
import { FilterSearchDynamicComponent } from '@/app/common/filters/types/searchDynamic/FilterSearchDynamicComponent.tsx';
import type { Suggestions } from '@/app/components/search/components/AutocompleteBasic';
import { URLParameters } from '@/app/common/urlParameters.ts';
import clientAPICall from '@/app/components/search/common/clientAPICall.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { createSuggestion } from '@/app/common/filters/surfaces/forms/custom/filterSearchDynamicUtils.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { CuriosityParams } from '@/app/components/search/Curiosity';
import type { AuthResponse } from '@/domainLogic/index.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { removeParameters } from '@/app/common/urlParametersUtils.ts';

type SuggestionFetcherCreatorProps = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  commonData: CommonData;
  localization: Localization;
  curiosityParams: CuriosityParams;
  authResponse: AuthResponse;
};

const suggestionFetcherCreator =
  ({ searchParams, searchInfoData, commonData, localization, curiosityParams }: SuggestionFetcherCreatorProps) =>
  async (query: string): Promise<Suggestions> => {
    const newQueryString = {
      ...removeParameters(searchParams, [URLParameters.MAP_AREA]),
      [URLParameters.ACCOMODATION_NAME]: query,
    };
    const payload: AutocompleteHotelPayload = {
      searchParams: newQueryString,
      searchInfoData,
      localization,
      commonData,
      curiosityParams,
    };
    const suggestionsRaw = await clientAPICall<AutocompleteHotelPayload, AutocompleteHotelAPIFullResponse>({
      apiName: API_NAME.AUTOCOMPLETE_HOTEL,
      payload,
      commonData,
      traceParent: searchInfoData.searchTraceParent,
    });

    const suggestions = (suggestionsRaw?.autocompleteHotels || [])
      .map((s: AutocompleteHotel) => ({ ...s, id: `${s.id}` }))
      .map(createSuggestion);

    return Promise.resolve(suggestions);
  };

export const FilterSearchDynamicPropertyName = ({ filter, handleFilterChanged }: FilterComponentProps) => {
  const { commonData, searchInfoData, localization, curiosityParams } = usePageWrapperData();
  const { authResponse } = commonData;
  const searchParamsFresh = getSearchParams();

  const handleFilterClicked = (selectedValue: string) => {
    const updatedFilter = {
      ...filter,
      data: {
        ...filter.data,
        value: selectedValue,
      } as FilterDataSearchDynamic,
    };
    handleFilterChanged([updatedFilter]);
  };

  const onUpdate = (e: FilterSearchDynamicSelected) => {
    handleFilterClicked(e.target.value);
  };

  const suggestionFetcher = suggestionFetcherCreator({
    searchParams: searchParamsFresh,
    searchInfoData,
    commonData,
    localization,
    curiosityParams,
    authResponse,
  });
  const props = {
    filter,
    name: FILTER_NAME.PROPERTY_NAME,
    suggestionFetcher,
    onUpdate,
  };

  return (
    <div>
      <FilterSearchDynamicComponent {...props} />
    </div>
  );
};
